<script lang="ts">
  // ConfirmDialog — the design-system confirm-this-action primitive; the host
  // mounts it only while open.
  import { Button, Markdown } from '$lib/ui';
  import type { ButtonVariant } from './Button.svelte';

  interface Props {
    title: string;
    body: string;
    note?: string;
    confirmLabel: string;
    confirmVariant?: ButtonVariant;
    cancelLabel: string;
    busy?: boolean;
    testid?: string;
    onConfirm: () => void;
    onCancel: () => void;
  }

  let {
    title,
    body,
    note,
    confirmLabel,
    confirmVariant = 'danger',
    cancelLabel,
    busy = false,
    testid,
    onConfirm,
    onCancel,
  }: Props = $props();

  const titleId = `confirm-title-${Math.random().toString(36).slice(2)}`;
  const bodyId = `confirm-body-${Math.random().toString(36).slice(2)}`;

  function onBackdropClick(e: MouseEvent): void {
    if (e.target === e.currentTarget && !busy) onCancel();
  }

  // Consumed, so a modal <dialog> hosting this confirm stays open.
  function onKeydown(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    if (!busy) onCancel();
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div
  class="confirm-backdrop"
  role="presentation"
  onclick={onBackdropClick}
>
  <div
    class="confirm-dialog"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby={titleId}
    aria-describedby={bodyId}
    data-testid={testid}
  >
    <h2 id={titleId}>{title}</h2>
    <p id={bodyId}>{body}</p>
    {#if note}
      <p class="confirm-note"><Markdown source={note} inline /></p>
    {/if}
    <div class="confirm-actions">
      <Button variant={confirmVariant} disabled={busy} onclick={onConfirm} data-testid="confirm-ok">
        {confirmLabel}
      </Button>
      <Button variant="ghost" disabled={busy} onclick={onCancel} data-testid="confirm-cancel">
        {cancelLabel}
      </Button>
    </div>
  </div>
</div>

<style lang="scss">
  .confirm-backdrop {
    position: fixed;
    inset: 0;
    background: rgb(0 0 0 / 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
  }

  .confirm-dialog {
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 1.25rem 1.4rem 1.4rem;
    max-width: 26rem;
    width: 92vw;
    box-shadow: 0 12px 36px rgb(0 0 0 / 0.2);
  }

  .confirm-dialog h2 {
    margin: 0 0 0.5rem;
    font-size: var(--text-guide);
  }

  .confirm-dialog p {
    margin: 0 0 1rem;
    color: var(--ink-soft);
    font-size: var(--text-body);
  }

  .confirm-dialog .confirm-note {
    font-size: var(--text-label);
  }

  .confirm-note :global(a) {
    color: var(--accent-text);
  }

  .confirm-actions {
    display: flex;
    gap: 0.5rem;
  }
</style>
